import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../App";
import { FileText, Plus, Send, Check, Eye, Clock, X } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contracts() {
  const { token } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newContract, setNewContract] = useState({
    project_id: "",
    title: "",
    content: "",
    contract_type: "agreement",
    valid_until: ""
  });

  useEffect(() => {
    fetchContracts();
    fetchProjects();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      }
    } catch (error) {
      console.error("Error fetching contracts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const handleCreateContract = async () => {
    if (!newContract.project_id || !newContract.title || !newContract.content) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/contracts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newContract)
      });

      if (response.ok) {
        toast.success("Contract created successfully");
        setShowCreate(false);
        setNewContract({ project_id: "", title: "", content: "", contract_type: "agreement", valid_until: "" });
        fetchContracts();
      } else {
        toast.error("Failed to create contract");
      }
    } catch (error) {
      toast.error("Error creating contract");
    }
  };

  const handleSendForSignature = async (contractId) => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${contractId}/send`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Contract sent for signature!");
        fetchContracts();
      } else {
        toast.error("Failed to send contract");
      }
    } catch (error) {
      toast.error("Error sending contract");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "signed": return <Check className="h-4 w-4 text-[#4CAF50]" />;
      case "sent": return <Send className="h-4 w-4 text-[#2196F3]" />;
      case "viewed": return <Eye className="h-4 w-4 text-[#FF6B35]" />;
      case "expired": return <X className="h-4 w-4 text-[#FF4444]" />;
      default: return <Clock className="h-4 w-4 text-[#888888]" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "signed": return "bg-[#4CAF50]/20 text-[#4CAF50]";
      case "sent": return "bg-[#2196F3]/20 text-[#2196F3]";
      case "viewed": return "bg-[#FF6B35]/20 text-[#FF6B35]";
      case "expired": return "bg-[#FF4444]/20 text-[#FF4444]";
      default: return "bg-[#888888]/20 text-[#888888]";
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="contracts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Contracts</h1>
          <p className="text-[#888888] text-sm mt-1">Create and manage digital contracts for client signatures</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF6B35] hover:bg-[#FF6B35]/90">
              <Plus className="h-4 w-4 mr-2" />
              New Contract
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#1A1A1A] border-[#262626] max-w-2xl max-h-[90dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Contract</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-[#888888] mb-1 block">Project *</label>
                <Select value={newContract.project_id} onValueChange={(v) => setNewContract({...newContract, project_id: v})}>
                  <SelectTrigger className="bg-[#111111] border-[#262626]">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project.project_id} value={project.project_id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-[#888888] mb-1 block">Contract Title *</label>
                <Input
                  value={newContract.title}
                  onChange={(e) => setNewContract({...newContract, title: e.target.value})}
                  placeholder="e.g., Architecture Services Agreement"
                  className="bg-[#111111] border-[#262626]"
                />
              </div>
              <div>
                <label className="text-sm text-[#888888] mb-1 block">Contract Type</label>
                <Select value={newContract.contract_type} onValueChange={(v) => setNewContract({...newContract, contract_type: v})}>
                  <SelectTrigger className="bg-[#111111] border-[#262626]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agreement">Agreement</SelectItem>
                    <SelectItem value="addendum">Addendum</SelectItem>
                    <SelectItem value="completion_certificate">Completion Certificate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-[#888888] mb-1 block">Valid Until</label>
                <Input
                  type="date"
                  value={newContract.valid_until}
                  onChange={(e) => setNewContract({...newContract, valid_until: e.target.value})}
                  className="bg-[#111111] border-[#262626]"
                />
              </div>
              <div>
                <label className="text-sm text-[#888888] mb-1 block">Contract Content *</label>
                <Textarea
                  value={newContract.content}
                  onChange={(e) => setNewContract({...newContract, content: e.target.value})}
                  placeholder="Enter the contract terms and conditions..."
                  className="bg-[#111111] border-[#262626] min-h-[200px]"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={handleCreateContract} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90">
                  Create Contract
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Contracts List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-40 bg-[#262626] rounded animate-pulse"></div>
          ))}
        </div>
      ) : contracts.length === 0 ? (
        <Card className="bg-[#1A1A1A] border-[#262626]">
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-[#888888] mx-auto mb-4" />
            <p className="text-[#888888]">No contracts yet. Create your first contract to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contracts.map(contract => (
            <Card key={contract.contract_id} className="bg-[#1A1A1A] border-[#262626] hover:border-[#FF6B35]/50 transition-colors">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded bg-[#262626] flex items-center justify-center">
                      <FileText className="h-5 w-5 text-[#FF6B35]" />
                    </div>
                    <div>
                      <CardTitle className="text-base text-white">{contract.title}</CardTitle>
                      <p className="text-[#888888] text-xs">{contract.contract_type.replace("_", " ").toUpperCase()}</p>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${getStatusColor(contract.status)}`}>
                    {getStatusIcon(contract.status)}
                    {contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-[#888888] text-sm line-clamp-2 mb-4">
                  {contract.content.substring(0, 150)}...
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-[#888888]">
                    {contract.valid_until && `Valid until: ${contract.valid_until}`}
                  </div>
                  <div className="flex gap-2">
                    {contract.status === "draft" && (
                      <Button
                        size="sm"
                        onClick={() => handleSendForSignature(contract.contract_id)}
                        className="bg-[#2196F3] hover:bg-[#2196F3]/90"
                      >
                        <Send className="h-3 w-3 mr-1" />
                        Send
                      </Button>
                    )}
                    {contract.status === "signed" && (
                      <Button size="sm" variant="outline" className="text-[#4CAF50] border-[#4CAF50]">
                        <Check className="h-3 w-3 mr-1" />
                        Signed
                      </Button>
                    )}
                  </div>
                </div>
                {contract.signed_at && (
                  <div className="mt-3 pt-3 border-t border-[#262626] text-xs text-[#888888]">
                    Signed by {contract.signed_by_name} on {new Date(contract.signed_at).toLocaleDateString()}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
